import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, MessageSquare, TrendingUp, Calendar, Sparkles, Mail, Phone, Building2, Eye, Download, Handshake, FileText, FileSpreadsheet, ArrowUpDown, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ConsultationRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  employeeCount: string;
  consultationType: string;
  preferredDate: string;
  message: string;
  status: "pending" | "contacted" | "completed" | "rejected";
  createdAt: string;
  adminMemo?: string;
}

interface ResourceRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  selectedResources: string[];
  status: "pending" | "sent" | "completed" | "rejected";
  createdAt: string;
  adminMemo?: string;
}

interface PartnershipRequest {
  id: string;
  name: string;
  email: string;
  company: string;
  partnershipType: string;
  message: string;
  status: "pending" | "reviewing" | "approved" | "rejected";
  createdAt: string;
  adminMemo?: string;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [adminEmail, setAdminEmail] = useState("");
  const [selectedConsultation, setSelectedConsultation] = useState<ConsultationRequest | null>(null);
  const [selectedResource, setSelectedResource] = useState<ResourceRequest | null>(null);
  const [selectedPartnership, setSelectedPartnership] = useState<PartnershipRequest | null>(null);
  const [isConsultationDetailOpen, setIsConsultationDetailOpen] = useState(false);
  const [isResourceDetailOpen, setIsResourceDetailOpen] = useState(false);
  const [isPartnershipDetailOpen, setIsPartnershipDetailOpen] = useState(false);
  const [consultationMemo, setConsultationMemo] = useState("");
  const [resourceMemo, setResourceMemo] = useState("");
  const [partnershipMemo, setPartnershipMemo] = useState("");
  
  // 정렬 상태
  const [consultationSort, setConsultationSort] = useState<{
    field: 'createdAt' | 'name' | 'company' | 'status';
    direction: 'asc' | 'desc';
  }>({ field: 'createdAt', direction: 'desc' });
  
  const [resourceSort, setResourceSort] = useState<{
    field: 'createdAt' | 'name' | 'company' | 'status';
    direction: 'asc' | 'desc';
  }>({ field: 'createdAt', direction: 'desc' });
  
  const [partnershipSort, setPartnershipSort] = useState<{
    field: 'createdAt' | 'name' | 'company' | 'status';
    direction: 'asc' | 'desc';
  }>({ field: 'createdAt', direction: 'desc' });

  // 데모 데이터
  const [consultations, setConsultations] = useState<ConsultationRequest[]>([
    {
      id: "1",
      name: "김철수",
      email: "kim@company.com",
      phone: "010-1234-5678",
      company: "(주)테크컴퍼니",
      position: "인사팀 팀장",
      employeeCount: "201-500",
      consultationType: "diagnosis",
      preferredDate: "2025-01-15",
      message: "AI 리터러시 진단에 대해 상담받고 싶습니다.",
      status: "pending",
      createdAt: "2026-01-10 14:30",
    },
    {
      id: "2",
      name: "이영희",
      email: "lee@startup.com",
      phone: "010-9876-5432",
      company: "스타트업코리아",
      position: "CEO",
      employeeCount: "1-50",
      consultationType: "training",
      preferredDate: "2025-01-20",
      message: "직원 AI 교육 프로그램 도입을 검토 중입니다.",
      status: "contacted",
      createdAt: "2026-01-09 10:15",
    },
    {
      id: "3",
      name: "박민수",
      email: "park@enterprise.com",
      phone: "010-5555-7777",
      company: "엔터프라이즈그룹",
      position: "교육담당 이사",
      employeeCount: "1000+",
      consultationType: "consulting",
      preferredDate: "2025-01-25",
      message: "전사 AI 역량 강화 컨설팅이 필요합니다.",
      status: "completed",
      createdAt: "2026-01-08 16:45",
    },
    {
      id: "4",
      name: "조영수",
      email: "cho@manufacturing.com",
      phone: "010-7777-8888",
      company: "제조업코리아",
      position: "기술부장",
      employeeCount: "501-1000",
      consultationType: "diagnosis",
      preferredDate: "2026-01-30",
      message: "제조업 특화 AI 진단을 원합니다.",
      status: "pending",
      createdAt: "2026-01-07 11:20",
    },
    {
      id: "5",
      name: "김다은",
      email: "kim@finance.com",
      phone: "010-9999-0000",
      company: "금융서비스",
      position: "HR 매니저",
      employeeCount: "101-200",
      consultationType: "training",
      preferredDate: "2026-02-05",
      message: "금융권 직원 AI 교육 프로그램을 찾고 있습니다.",
      status: "contacted",
      createdAt: "2026-01-06 09:45",
    },
  ]);

  // 자료 요청 데모 데이터
  const [resourceRequests, setResourceRequests] = useState<ResourceRequest[]>([
    {
      id: "1",
      name: "최지우",
      email: "choi@company.com",
      phone: "010-2222-3333",
      company: "(주)디지털컴퍼니",
      selectedResources: ["KAILA 프레임워크 소개서", "AI 규제 가이드"],
      status: "pending",
      createdAt: "2026-01-11 09:20",
    },
    {
      id: "2",
      name: "정수진",
      email: "jung@tech.com",
      phone: "010-3333-4444",
      company: "테크솔루션즈",
      selectedResources: ["진단 사례 연구"],
      status: "sent",
      createdAt: "2026-01-10 15:30",
    },
    {
      id: "3",
      name: "이준호",
      email: "lee@healthcare.com",
      phone: "010-4444-5555",
      company: "헬스케어플러스",
      selectedResources: ["KAILA 프레임워크 소개서", "진단 사례 연구", "AI 규제 가이드"],
      status: "pending",
      createdAt: "2026-01-09 13:15",
    },
    {
      id: "4",
      name: "박지은",
      email: "park@retail.com",
      phone: "010-6666-7777",
      company: "리테일인노베이션",
      selectedResources: ["진단 사례 연구"],
      status: "completed",
      createdAt: "2026-01-08 14:30",
    },
  ]);

  // 파트너십 요청 데모 데이터
  const [partnershipRequests, setPartnershipRequests] = useState<PartnershipRequest[]>([
    {
      id: "1",
      name: "강하나",
      email: "kang@partner.com",
      company: "(주)파트너컴퍼니",
      partnershipType: "corporate",
      message: "기업 파트너로 협력하고 싶습니다.",
      status: "pending",
      createdAt: "2026-01-11 11:00",
    },
    {
      id: "2",
      name: "윤서준",
      email: "yoon@university.edu",
      company: "한국대학교",
      partnershipType: "education",
      message: "교육 기관 파트너십을 제안합니다.",
      status: "reviewing",
      createdAt: "2026-01-09 14:20",
    },
    {
      id: "3",
      name: "정우진",
      email: "jung@global.com",
      company: "글로벌인노베이션",
      partnershipType: "global",
      message: "해외 시장 진출을 위한 글로벌 파트너십을 제안합니다.",
      status: "approved",
      createdAt: "2026-01-08 10:30",
    },
    {
      id: "4",
      name: "최민수",
      email: "choi@tech.com",
      company: "테크이노베이션",
      partnershipType: "technology",
      message: "AI 기술 플랫폼 연동을 위한 기술 파트너십을 원합니다.",
      status: "pending",
      createdAt: "2026-01-07 15:45",
    },
  ]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isAdminLoggedIn");
    const email = localStorage.getItem("adminEmail");

    if (!isLoggedIn || isLoggedIn !== "true") {
      navigate("/admin/login");
      return;
    }

    if (email) {
      setAdminEmail(email);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("isAdminLoggedIn");
    localStorage.removeItem("adminEmail");
    toast({
      title: "로그아웃",
      description: "로그아웃되었습니다.",
    });
    navigate("/admin/login");
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">대기중</Badge>;
      case "contacted":
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">연락완료</Badge>;
      case "completed":
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">완료</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">거절</Badge>;
      default:
        return <Badge variant="outline">알 수 없음</Badge>;
    }
  };

  const getConsultationTypeName = (type: string) => {
    switch (type) {
      case "diagnosis":
        return "AI 리터러시 진단";
      case "training":
        return "AI 교육 프로그램";
      case "consulting":
        return "컨설팅";
      case "partnership":
        return "파트너십";
      default:
        return "기타";
    }
  };

  const handleViewConsultationDetail = (consultation: ConsultationRequest) => {
    setSelectedConsultation(consultation);
    setConsultationMemo(consultation.adminMemo || "");
    setIsConsultationDetailOpen(true);
  };

  const handleViewResourceDetail = (resource: ResourceRequest) => {
    setSelectedResource(resource);
    setResourceMemo(resource.adminMemo || "");
    setIsResourceDetailOpen(true);
  };

  const handleViewPartnershipDetail = (partnership: PartnershipRequest) => {
    setSelectedPartnership(partnership);
    setPartnershipMemo(partnership.adminMemo || "");
    setIsPartnershipDetailOpen(true);
  };

  // 정렬 함수
  const handleConsultationSort = (field: 'createdAt' | 'name' | 'company' | 'status') => {
    setConsultationSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleResourceSort = (field: 'createdAt' | 'name' | 'company' | 'status') => {
    setResourceSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePartnershipSort = (field: 'createdAt' | 'name' | 'company' | 'status') => {
    setPartnershipSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // 정렬된 데이터
  const sortedConsultations = [...consultations].sort((a, b) => {
    const { field, direction } = consultationSort;
    let comparison = 0;
    
    if (field === 'createdAt') {
      comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    } else if (field === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (field === 'company') {
      comparison = a.company.localeCompare(b.company);
    } else if (field === 'status') {
      comparison = a.status.localeCompare(b.status);
    }
    
    return direction === 'asc' ? comparison : -comparison;
  });

  const sortedResources = [...resourceRequests].sort((a, b) => {
    const { field, direction } = resourceSort;
    let comparison = 0;
    
    if (field === 'createdAt') {
      comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    } else if (field === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (field === 'company') {
      comparison = a.company.localeCompare(b.company);
    } else if (field === 'status') {
      comparison = a.status.localeCompare(b.status);
    }
    
    return direction === 'asc' ? comparison : -comparison;
  });

  const sortedPartnerships = [...partnershipRequests].sort((a, b) => {
    const { field, direction } = partnershipSort;
    let comparison = 0;
    
    if (field === 'createdAt') {
      comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    } else if (field === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (field === 'company') {
      comparison = a.company.localeCompare(b.company);
    } else if (field === 'status') {
      comparison = a.status.localeCompare(b.status);
    }
    
    return direction === 'asc' ? comparison : -comparison;
  });

  // 정렬 아이콘 표시
  const getSortIcon = (field: string, currentSort: { field: string; direction: 'asc' | 'desc' }) => {
    if (currentSort.field !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 inline" />;
    }
    return currentSort.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 inline" />
      : <ArrowDown className="h-4 w-4 ml-1 inline" />;
  };

  // 메모 저장 함수
  const handleSaveConsultationMemo = (id: string, memo: string) => {
    setConsultations(prev => 
      prev.map(c => c.id === id ? { ...c, adminMemo: memo } : c)
    );
    toast({
      title: "메모 저장 완료",
      description: "관리자 메모가 저장되었습니다.",
    });
  };

  const handleSaveResourceMemo = (id: string, memo: string) => {
    setResourceRequests(prev => 
      prev.map(r => r.id === id ? { ...r, adminMemo: memo } : r)
    );
    toast({
      title: "메모 저장 완료",
      description: "관리자 메모가 저장되었습니다.",
    });
  };

  const handleSavePartnershipMemo = (id: string, memo: string) => {
    setPartnershipRequests(prev => 
      prev.map(p => p.id === id ? { ...p, adminMemo: memo } : p)
    );
    toast({
      title: "메모 저장 완료",
      description: "관리자 메모가 저장되었습니다.",
    });
  };

  // 삭제 함수
  const handleDeleteConsultation = (id: string) => {
    if (window.confirm('정말로 이 상담 신청을 삭제하시겠습니까?')) {
      setConsultations(prev => prev.filter(c => c.id !== id));
      toast({
        title: "삭제 완료",
        description: "상담 신청이 삭제되었습니다.",
      });
      // 상세 보기 모달 닫기
      setIsConsultationDetailOpen(false);
    }
  };

  const handleDeleteResource = (id: string) => {
    if (window.confirm('정말로 이 자료 요청을 삭제하시겠습니까?')) {
      setResourceRequests(prev => prev.filter(r => r.id !== id));
      toast({
        title: "삭제 완료",
        description: "자료 요청이 삭제되었습니다.",
      });
      // 상세 보기 모달 닫기
      setIsResourceDetailOpen(false);
    }
  };

  const handleDeletePartnership = (id: string) => {
    if (window.confirm('정말로 이 파트너십 요청을 삭제하시겠습니까?')) {
      setPartnershipRequests(prev => prev.filter(p => p.id !== id));
      toast({
        title: "삭제 완료",
        description: "파트너십 요청이 삭제되었습니다.",
      });
      // 상세 보기 모달 닫기
      setIsPartnershipDetailOpen(false);
    }
  };

  // 상태 변경 함수
  const handleConsultationStatusChange = (id: string, newStatus: ConsultationRequest['status']) => {
    setConsultations(prev => 
      prev.map(c => c.id === id ? { ...c, status: newStatus } : c)
    );
    toast({
      title: "상태 변경 완료",
      description: `상담 신청 상태가 변경되었습니다.`,
    });
    setIsConsultationDetailOpen(false);
  };

  const handleResourceStatusChange = (id: string, newStatus: ResourceRequest['status']) => {
    setResourceRequests(prev => 
      prev.map(r => r.id === id ? { ...r, status: newStatus } : r)
    );
    toast({
      title: "상태 변경 완료",
      description: `자료 요청 상태가 변경되었습니다.`,
    });
    setIsResourceDetailOpen(false);
  };

  const handlePartnershipStatusChange = (id: string, newStatus: PartnershipRequest['status']) => {
    setPartnershipRequests(prev => 
      prev.map(p => p.id === id ? { ...p, status: newStatus } : p)
    );
    toast({
      title: "상태 변경 완료",
      description: `파트너십 요청 상태가 변경되었습니다.`,
    });
    setIsPartnershipDetailOpen(false);
  };

  // 엑셀 출력 함수
  const exportToExcel = (type: 'consultation' | 'resource' | 'partnership' | 'all') => {
    let csvContent = "";
    let filename = "";

    if (type === 'consultation' || type === 'all') {
      csvContent += "상담 신청 목록\n";
      csvContent += "신청일시,이름,이메일,전화번호,회사,직책,직원수,상담유형,희망일자,문의내용,상태\n";
      consultations.forEach(c => {
        csvContent += `${c.createdAt},${c.name},${c.email},${c.phone},${c.company},${c.position},${c.employeeCount},${getConsultationTypeName(c.consultationType)},${c.preferredDate},"${c.message}",${c.status}\n`;
      });
      csvContent += "\n";
    }

    if (type === 'resource' || type === 'all') {
      csvContent += "자료 요청 목록\n";
      csvContent += "신청일시,이름,이메일,전화번호,회사,요청자료,상태\n";
      resourceRequests.forEach(r => {
        csvContent += `${r.createdAt},${r.name},${r.email},${r.phone},${r.company},"${r.selectedResources.join(', ')}",${r.status}\n`;
      });
      csvContent += "\n";
    }

    if (type === 'partnership' || type === 'all') {
      csvContent += "파트너십 요청 목록\n";
      csvContent += "신청일시,이름,이메일,회사/기관,파트너십유형,문의내용,상태\n";
      partnershipRequests.forEach(p => {
        const typeText = p.partnershipType === "corporate" ? "기업" : p.partnershipType === "education" ? "교육" : p.partnershipType === "global" ? "글로벌" : p.partnershipType === "technology" ? "기술" : "기타";
        csvContent += `${p.createdAt},${p.name},${p.email},${p.company},${typeText},"${p.message}",${p.status}\n`;
      });
    }

    filename = type === 'all' ? 'KAILA_전체요청목록.csv' : 
               type === 'consultation' ? 'KAILA_상담신청.csv' :
               type === 'resource' ? 'KAILA_자료요청.csv' : 'KAILA_파트너십.csv';

    // BOM 추가로 한글 깨짐 방지
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "엑셀 다운로드 완료",
      description: `${filename} 파일이 다운로드되었습니다.`,
    });
  };

  const consultationStats = {
    total: consultations.length,
    pending: consultations.filter(c => c.status === "pending").length,
    contacted: consultations.filter(c => c.status === "contacted").length,
    completed: consultations.filter(c => c.status === "completed").length,
  };

  const resourceStats = {
    total: resourceRequests.length,
    pending: resourceRequests.filter(r => r.status === "pending").length,
    sent: resourceRequests.filter(r => r.status === "sent").length,
  };

  const partnershipStats = {
    total: partnershipRequests.length,
    pending: partnershipRequests.filter(p => p.status === "pending").length,
    reviewing: partnershipRequests.filter(p => p.status === "reviewing").length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold">KAILA Admin</span>
                <p className="text-xs text-muted-foreground">{adminEmail}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/")}>
                홈으로
              </Button>
              <ThemeToggle />
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                로그아웃
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">관리자 대시보드</h1>
          <p className="text-muted-foreground">상담 신청, 자료 요청, 파트너십 현황을 관리하세요</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-2 border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-transparent">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">상담 신청</CardTitle>
                <MessageSquare className="h-5 w-5 text-blue-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="text-3xl font-bold">{consultationStats.total}건</div>
                <div className="flex gap-2 text-sm">
                  <Badge variant="outline" className="bg-yellow-500/10">대기 {consultationStats.pending}</Badge>
                  <Badge variant="outline" className="bg-blue-500/10">연락완료 {consultationStats.contacted}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-transparent">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">자료 요청</CardTitle>
                <Download className="h-5 w-5 text-purple-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="text-3xl font-bold">{resourceStats.total}건</div>
                <div className="flex gap-2 text-sm">
                  <Badge variant="outline" className="bg-yellow-500/10">대기 {resourceStats.pending}</Badge>
                  <Badge variant="outline" className="bg-green-500/10">전송완료 {resourceStats.sent}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-500/20 bg-gradient-to-br from-green-500/5 to-transparent">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">파트너십</CardTitle>
                <Handshake className="h-5 w-5 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="text-3xl font-bold">{partnershipStats.total}건</div>
                <div className="flex gap-2 text-sm">
                  <Badge variant="outline" className="bg-yellow-500/10">대기 {partnershipStats.pending}</Badge>
                  <Badge variant="outline" className="bg-blue-500/10">검토중 {partnershipStats.reviewing}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different request types */}
        <Card className="border-2 border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>요청 관리</CardTitle>
                <CardDescription>모든 요청 내역을 확인하고 관리하세요</CardDescription>
              </div>
              <Button 
                variant="outline" 
                className="bg-green-500/10 hover:bg-green-500/20 border-green-500/20"
                onClick={() => exportToExcel('all')}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                전체 엑셀 다운로드
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="consultation" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="consultation">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  상담 신청 ({consultationStats.total})
                </TabsTrigger>
                <TabsTrigger value="resources">
                  <Download className="h-4 w-4 mr-2" />
                  자료 요청 ({resourceStats.total})
                </TabsTrigger>
                <TabsTrigger value="partnership">
                  <Handshake className="h-4 w-4 mr-2" />
                  파트너십 ({partnershipStats.total})
                </TabsTrigger>
              </TabsList>

              {/* Consultation Tab */}
              <TabsContent value="consultation" className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">상담 신청 목록</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportToExcel('consultation')}
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    엑셀 다운로드
                  </Button>
                </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleConsultationSort('createdAt')}
                  >
                    신청일시
                    {getSortIcon('createdAt', consultationSort)}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleConsultationSort('name')}
                  >
                    이름
                    {getSortIcon('name', consultationSort)}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleConsultationSort('company')}
                  >
                    회사
                    {getSortIcon('company', consultationSort)}
                  </TableHead>
                  <TableHead>상담유형</TableHead>
                  <TableHead>희망일자</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleConsultationSort('status')}
                  >
                    상태
                    {getSortIcon('status', consultationSort)}
                  </TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedConsultations.map((consultation) => (
                  <TableRow key={consultation.id}>
                    <TableCell className="font-medium">{consultation.createdAt}</TableCell>
                    <TableCell>{consultation.name}</TableCell>
                    <TableCell>{consultation.company}</TableCell>
                    <TableCell>{getConsultationTypeName(consultation.consultationType)}</TableCell>
                    <TableCell>{consultation.preferredDate || "-"}</TableCell>
                    <TableCell>{getStatusBadge(consultation.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewConsultationDetail(consultation)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          상세보기
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteConsultation(consultation.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">자료 요청 목록</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => exportToExcel('resource')}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                엑셀 다운로드
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleResourceSort('createdAt')}
                  >
                    신청일시
                    {getSortIcon('createdAt', resourceSort)}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleResourceSort('name')}
                  >
                    이름
                    {getSortIcon('name', resourceSort)}
                  </TableHead>
                  <TableHead>전화번호</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleResourceSort('company')}
                  >
                    회사
                    {getSortIcon('company', resourceSort)}
                  </TableHead>
                  <TableHead>요청 자료</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleResourceSort('status')}
                  >
                    상태
                    {getSortIcon('status', resourceSort)}
                  </TableHead>
                  <TableHead>작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedResources.map((resource) => (
                  <TableRow key={resource.id}>
                    <TableCell className="font-medium">{resource.createdAt}</TableCell>
                    <TableCell>{resource.name}</TableCell>
                    <TableCell>{resource.phone}</TableCell>
                    <TableCell>{resource.company}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {resource.selectedResources.map((res, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {res.length > 15 ? res.substring(0, 15) + '...' : res}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {resource.status === "pending" && (
                        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400">
                          대기중
                        </Badge>
                      )}
                      {resource.status === "sent" && (
                        <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400">
                          전송완료
                        </Badge>
                      )}
                      {resource.status === "completed" && (
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400">
                          완료
                        </Badge>
                      )}
                      {resource.status === "rejected" && (
                        <Badge variant="outline" className="bg-red-500/10 text-red-700 dark:text-red-400">
                          거절
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewResourceDetail(resource)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          상세보기
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteResource(resource.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          {/* Partnership Tab */}
          <TabsContent value="partnership" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">파트너십 요청 목록</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => exportToExcel('partnership')}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                엑셀 다운로드
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handlePartnershipSort('createdAt')}
                  >
                    신청일시
                    {getSortIcon('createdAt', partnershipSort)}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handlePartnershipSort('name')}
                  >
                    이름
                    {getSortIcon('name', partnershipSort)}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handlePartnershipSort('company')}
                  >
                    회사/기관
                    {getSortIcon('company', partnershipSort)}
                  </TableHead>
                  <TableHead>파트너십 유형</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handlePartnershipSort('status')}
                  >
                    상태
                    {getSortIcon('status', partnershipSort)}
                  </TableHead>
                  <TableHead>작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPartnerships.map((partnership) => (
                  <TableRow key={partnership.id}>
                    <TableCell className="font-medium">{partnership.createdAt}</TableCell>
                    <TableCell>{partnership.name}</TableCell>
                    <TableCell>{partnership.company}</TableCell>
                    <TableCell>
                      {partnership.partnershipType === "corporate" && "기업 파트너"}
                      {partnership.partnershipType === "education" && "교육 기관"}
                      {partnership.partnershipType === "global" && "글로벌 파트너"}
                      {partnership.partnershipType === "technology" && "기술 파트너"}
                      {partnership.partnershipType === "other" && "기타"}
                    </TableCell>
                    <TableCell>
                      {partnership.status === "pending" && (
                        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400">
                          대기중
                        </Badge>
                      )}
                      {partnership.status === "reviewing" && (
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400">
                          검토중
                        </Badge>
                      )}
                      {partnership.status === "approved" && (
                        <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400">
                          승인
                        </Badge>
                      )}
                      {partnership.status === "rejected" && (
                        <Badge variant="outline" className="bg-red-500/10 text-red-700 dark:text-red-400">
                          거절
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewPartnershipDetail(partnership)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          상세보기
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePartnership(partnership.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  </div>

      {/* Consultation Detail Dialog */}
      <Dialog open={isConsultationDetailOpen} onOpenChange={setIsConsultationDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>상담 신청 상세 정보</DialogTitle>
            <DialogDescription>
              신청 ID: {selectedConsultation?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedConsultation && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    이름
                  </Label>
                  <p className="font-medium">{selectedConsultation.name}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    회사
                  </Label>
                  <p className="font-medium">{selectedConsultation.company}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    이메일
                  </Label>
                  <p className="font-medium">{selectedConsultation.email}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    전화번호
                  </Label>
                  <p className="font-medium">{selectedConsultation.phone || "-"}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">직책</Label>
                  <p className="font-medium">{selectedConsultation.position || "-"}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">직원 수</Label>
                  <p className="font-medium">{selectedConsultation.employeeCount || "-"}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">상담 유형</Label>
                  <p className="font-medium">{getConsultationTypeName(selectedConsultation.consultationType)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    희망 일자
                  </Label>
                  <p className="font-medium">{selectedConsultation.preferredDate || "-"}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground">
                  <MessageSquare className="h-4 w-4" />
                  문의 내용
                </Label>
                <p className="font-medium bg-muted/50 p-4 rounded-lg">
                  {selectedConsultation.message || "문의 내용이 없습니다."}
                </p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>상태 변경</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant={selectedConsultation.status === "pending" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleConsultationStatusChange(selectedConsultation.id, "pending")}
                      className={selectedConsultation.status === "pending" ? "bg-yellow-500 hover:bg-yellow-600" : ""}
                    >
                      대기중
                    </Button>
                    <Button 
                      variant={selectedConsultation.status === "contacted" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleConsultationStatusChange(selectedConsultation.id, "contacted")}
                      className={selectedConsultation.status === "contacted" ? "bg-blue-500 hover:bg-blue-600" : ""}
                    >
                      연락완료
                    </Button>
                    <Button 
                      variant={selectedConsultation.status === "completed" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleConsultationStatusChange(selectedConsultation.id, "completed")}
                      className={selectedConsultation.status === "completed" ? "bg-green-500 hover:bg-green-600" : ""}
                    >
                      완료
                    </Button>
                    <Button 
                      variant={selectedConsultation.status === "rejected" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleConsultationStatusChange(selectedConsultation.id, "rejected")}
                      className={selectedConsultation.status === "rejected" ? "bg-red-500 hover:bg-red-600" : ""}
                    >
                      거절
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>관리자 메모</Label>
                  <textarea
                    className="w-full min-h-[100px] p-3 border rounded-lg bg-background"
                    placeholder="내부 관리용 메모를 입력하세요..."
                    value={consultationMemo}
                    onChange={(e) => setConsultationMemo(e.target.value)}
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleSaveConsultationMemo(selectedConsultation.id, consultationMemo)}
                  >
                    메모 저장
                  </Button>
                </div>
                <div className="flex justify-between">
                  <Button 
                    variant="destructive"
                    onClick={() => selectedConsultation && handleDeleteConsultation(selectedConsultation.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    삭제
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setIsConsultationDetailOpen(false)}>
                      닫기
                    </Button>
                    <Button 
                      className="bg-blue-500 hover:bg-blue-600"
                      onClick={() => {
                        const subject = encodeURIComponent(`[KAILA] ${selectedConsultation.name}님 상담 신청 관련`);
                        const body = encodeURIComponent(
                          `안녕하세요 ${selectedConsultation.name}님,\n\n` +
                          `KAILA 프레임워크 상담 신청에 감사드립니다.\n\n` +
                          `[신청 정보]\n` +
                          `- 회사: ${selectedConsultation.company}\n` +
                          `- 직책: ${selectedConsultation.position}\n` +
                          `- 상담 유형: ${getConsultationTypeName(selectedConsultation.consultationType)}\n` +
                          `- 희망 일자: ${selectedConsultation.preferredDate}\n\n` +
                          `문의 내용:\n${selectedConsultation.message}\n\n` +
                          `감사합니다.\n\n` +
                          `KAILA 팀 드림`
                        );
                        window.open(
                          `https://mail.google.com/mail/?view=cm&fs=1&to=${selectedConsultation.email}&su=${subject}&body=${body}&bcc=ernest@ailiteracy.or.kr`,
                          '_blank'
                        );
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Gmail로 보내기
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resource Detail Dialog */}
      <Dialog open={isResourceDetailOpen} onOpenChange={setIsResourceDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>자료 요청 상세 정보</DialogTitle>
            <DialogDescription>
              요청 ID: {selectedResource?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedResource && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    이름
                  </Label>
                  <p className="font-medium">{selectedResource.name}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    회사
                  </Label>
                  <p className="font-medium">{selectedResource.company}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    이메일
                  </Label>
                  <p className="font-medium">{selectedResource.email}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    전화번호
                  </Label>
                  <p className="font-medium">{selectedResource.phone}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">신청일시</Label>
                  <p className="font-medium">{selectedResource.createdAt}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  요청 자료
                </Label>
                <div className="flex flex-wrap gap-2">
                  {selectedResource.selectedResources.map((res, idx) => (
                    <Badge key={idx} variant="outline" className="bg-blue-500/10">
                      {res}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>상태 변경</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant={selectedResource.status === "pending" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleResourceStatusChange(selectedResource.id, "pending")}
                      className={selectedResource.status === "pending" ? "bg-yellow-500 hover:bg-yellow-600" : ""}
                    >
                      대기중
                    </Button>
                    <Button 
                      variant={selectedResource.status === "sent" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleResourceStatusChange(selectedResource.id, "sent")}
                      className={selectedResource.status === "sent" ? "bg-green-500 hover:bg-green-600" : ""}
                    >
                      전송완료
                    </Button>
                    <Button 
                      variant={selectedResource.status === "completed" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleResourceStatusChange(selectedResource.id, "completed")}
                      className={selectedResource.status === "completed" ? "bg-blue-500 hover:bg-blue-600" : ""}
                    >
                      완료
                    </Button>
                    <Button 
                      variant={selectedResource.status === "rejected" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleResourceStatusChange(selectedResource.id, "rejected")}
                      className={selectedResource.status === "rejected" ? "bg-red-500 hover:bg-red-600" : ""}
                    >
                      거절
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>관리자 메모</Label>
                  <textarea
                    className="w-full min-h-[100px] p-3 border rounded-lg bg-background"
                    placeholder="내부 관리용 메모를 입력하세요..."
                    value={resourceMemo}
                    onChange={(e) => setResourceMemo(e.target.value)}
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleSaveResourceMemo(selectedResource.id, resourceMemo)}
                  >
                    메모 저장
                  </Button>
                </div>
                <div className="flex justify-between">
                  <Button 
                    variant="destructive"
                    onClick={() => selectedResource && handleDeleteResource(selectedResource.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    삭제
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setIsResourceDetailOpen(false)}>
                      닫기
                    </Button>
                    <Button 
                      className="bg-purple-500 hover:bg-purple-600"
                      onClick={() => {
                        const subject = encodeURIComponent(`[KAILA] ${selectedResource.name}님 자료 요청 관련`);
                        const resourceList = selectedResource.selectedResources.join('\n- ');
                        const body = encodeURIComponent(
                          `안녕하세요 ${selectedResource.name}님,\n\n` +
                          `KAILA 프레임워크 자료 요청에 감사드립니다.\n\n` +
                          `[요청 정보]\n` +
                          `- 회사: ${selectedResource.company}\n` +
                          `- 전화번호: ${selectedResource.phone}\n\n` +
                          `[요청 자료]\n- ${resourceList}\n\n` +
                          `요청하신 자료를 첨부하여 보내드립니다.\n\n` +
                          `감사합니다.\n\n` +
                          `KAILA 팀 드림`
                        );
                        window.open(
                          `https://mail.google.com/mail/?view=cm&fs=1&to=${selectedResource.email}&su=${subject}&body=${body}&bcc=ernest@ailiteracy.or.kr`,
                          '_blank'
                        );
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Gmail로 보내기
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Partnership Detail Dialog */}
      <Dialog open={isPartnershipDetailOpen} onOpenChange={setIsPartnershipDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>파트너십 요청 상세 정보</DialogTitle>
            <DialogDescription>
              요청 ID: {selectedPartnership?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedPartnership && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    이름
                  </Label>
                  <p className="font-medium">{selectedPartnership.name}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    회사/기관
                  </Label>
                  <p className="font-medium">{selectedPartnership.company}</p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    이메일
                  </Label>
                  <p className="font-medium">{selectedPartnership.email}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">파트너십 유형</Label>
                  <p className="font-medium">
                    {selectedPartnership.partnershipType === "corporate" && "기업 파트너"}
                    {selectedPartnership.partnershipType === "education" && "교육 기관"}
                    {selectedPartnership.partnershipType === "global" && "글로벌 파트너"}
                    {selectedPartnership.partnershipType === "technology" && "기술 파트너"}
                    {selectedPartnership.partnershipType === "other" && "기타"}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground">
                  <MessageSquare className="h-4 w-4" />
                  문의 내용
                </Label>
                <p className="font-medium bg-muted/50 p-4 rounded-lg">
                  {selectedPartnership.message || "문의 내용이 없습니다."}
                </p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>상태 변경</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant={selectedPartnership.status === "pending" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePartnershipStatusChange(selectedPartnership.id, "pending")}
                      className={selectedPartnership.status === "pending" ? "bg-yellow-500 hover:bg-yellow-600" : ""}
                    >
                      대기중
                    </Button>
                    <Button 
                      variant={selectedPartnership.status === "reviewing" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePartnershipStatusChange(selectedPartnership.id, "reviewing")}
                      className={selectedPartnership.status === "reviewing" ? "bg-blue-500 hover:bg-blue-600" : ""}
                    >
                      검토중
                    </Button>
                    <Button 
                      variant={selectedPartnership.status === "approved" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePartnershipStatusChange(selectedPartnership.id, "approved")}
                      className={selectedPartnership.status === "approved" ? "bg-green-500 hover:bg-green-600" : ""}
                    >
                      승인
                    </Button>
                    <Button 
                      variant={selectedPartnership.status === "rejected" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePartnershipStatusChange(selectedPartnership.id, "rejected")}
                      className={selectedPartnership.status === "rejected" ? "bg-red-500 hover:bg-red-600" : ""}
                    >
                      거절
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>관리자 메모</Label>
                  <textarea
                    className="w-full min-h-[100px] p-3 border rounded-lg bg-background"
                    placeholder="내부 관리용 메모를 입력하세요..."
                    value={partnershipMemo}
                    onChange={(e) => setPartnershipMemo(e.target.value)}
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleSavePartnershipMemo(selectedPartnership.id, partnershipMemo)}
                  >
                    메모 저장
                  </Button>
                </div>
                <div className="flex justify-between">
                  <Button 
                    variant="destructive"
                    onClick={() => selectedPartnership && handleDeletePartnership(selectedPartnership.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    삭제
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setIsPartnershipDetailOpen(false)}>
                      닫기
                    </Button>
                    <Button 
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => {
                        const partnershipTypeText = 
                          selectedPartnership.partnershipType === "corporate" ? "기업 파트너" :
                          selectedPartnership.partnershipType === "education" ? "교육 기관" :
                          selectedPartnership.partnershipType === "global" ? "글로벌 파트너" :
                          selectedPartnership.partnershipType === "technology" ? "기술 파트너" : "기타";
                        
                        const subject = encodeURIComponent(`[KAILA] ${selectedPartnership.name}님 파트너십 제안 관련`);
                        const body = encodeURIComponent(
                          `안녕하세요 ${selectedPartnership.name}님,\n\n` +
                          `KAILA 프레임워크 파트너십 제안에 감사드립니다.\n\n` +
                          `[제안 정보]\n` +
                          `- 회사/기관: ${selectedPartnership.company}\n` +
                          `- 파트너십 유형: ${partnershipTypeText}\n\n` +
                          `문의 내용:\n${selectedPartnership.message}\n\n` +
                          `귀사와의 협력 기회를 검토하고 있으며, 자세한 논의를 위해 연락드립니다.\n\n` +
                          `감사합니다.\n\n` +
                          `KAILA 팀 드림`
                        );
                        window.open(
                          `https://mail.google.com/mail/?view=cm&fs=1&to=${selectedPartnership.email}&su=${subject}&body=${body}&bcc=ernest@ailiteracy.or.kr`,
                          '_blank'
                        );
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Gmail로 보내기
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Label = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`text-sm font-medium ${className}`}>{children}</div>
);

export default AdminDashboard;
